import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";

interface ClientListItem {
  id: string;
  name: string;
  is_minor: boolean;
}

type Status = "checking" | "active" | "pending" | "skipped";

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

/**
 * Where Stripe Checkout's success_url lands the browser (PRD §4.2 step 4: "shows a brief 'processing'
 * state... does not let account creation complete with billing silently left unresolved"). The webhook
 * that actually flips billing_status to 'active' can arrive a moment after this redirect, so this polls
 * rather than trusting the redirect alone as proof of payment.
 */
export function SignupBillingComplete() {
  const [status, setStatus] = useState<Status>("checking");
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<ClientListItem | null>(null);
  const [minorUsername, setMinorUsername] = useState("");
  const [minorPassword, setMinorPassword] = useState("");
  const [minorSetupDone, setMinorSetupDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function poll(attempt: number) {
      try {
        const { billingStatus } = await apiGet<{ billingStatus: string }>("/billing/status");
        if (cancelled) return;
        if (billingStatus === "active") {
          setStatus("active");
          const clients = await apiGet<ClientListItem[]>("/clients");
          if (!cancelled && clients[0]) setClient(clients[0]);
          return;
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to check billing status");
      }

      if (attempt >= MAX_POLL_ATTEMPTS) {
        if (!cancelled) setStatus("pending");
        return;
      }
      setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
    }

    poll(0);
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleCreateMinorLogin(event: FormEvent) {
    event.preventDefault();
    if (!client) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiPost(`/clients/${client.id}/login`, { username: minorUsername, password: minorPassword });
      setMinorSetupDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create the athlete's login");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="centered-page">
      <div className="card signup-card">
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}

        {status === "checking" && <p>Confirming your payment…</p>}

        {status === "pending" && (
          <>
            <p>Your payment is still processing.</p>
            <p>If you paid by card, this is usually just a moment -- try checking again. If you chose a bank account (ACH), confirmation can take a few business days; feel free to come back later.</p>
            <button onClick={() => window.location.reload()}>Check again</button>
          </>
        )}

        {/* Confirm first, ask second.
            The success page led straight into "Set up <name>'s login" -- a form -- so the one thing a
            person needs to know at that moment, that their money went through and their account is
            real, was never actually said. They had just handed over a card to a brand they met
            yesterday (UX review F16). */}
        {status === "active" && (
          <p className="badge badge-ok" role="status">
            You&rsquo;re all set &mdash; your payment went through and your account is active.
          </p>
        )}

        {status === "active" && client?.is_minor && !minorSetupDone && (
          <form onSubmit={handleCreateMinorLogin}>
            <h1>One last thing: set up {client.name}'s login</h1>
            <p>Give {client.name} their own username and password now, or skip and do this later from account settings.</p>
            <label htmlFor="minor-username">Username</label>
            <input id="minor-username" value={minorUsername} onChange={(e) => setMinorUsername(e.target.value)} required />

            <label htmlFor="minor-password">Password</label>
            <input id="minor-password" type="password" value={minorPassword} onChange={(e) => setMinorPassword(e.target.value)} required minLength={8} />

            <button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Create login"}
            </button>
            <button type="button" className="chip" onClick={() => setMinorSetupDone(true)}>
              Skip for now
            </button>
          </form>
        )}

        {status === "active" && (!client?.is_minor || minorSetupDone) && (
          <>
            <h1>You're all set!</h1>
            <button onClick={() => navigate("/family")}>Go to your dashboard</button>
          </>
        )}
      </div>
    </div>
  );
}
